<template>
  <form @submit.prevent novalidate="true">
    <b-card>
      <template slot="header">
        <div class="form-group form-row mb-0">
          <div class="col-lg-6">
            <treeselect :value="surveyValue"
                        :defaultExpandLevel="Infinity"
                        :clearable="false"
                        :placeholder="$t('Webportal.StructureFilter.Placeholder')"
                        :options="structureItems"
                        :disableFuzzyMatching="true"
                        :disable-branch-nodes="true"
                        :disabled="isBusy"
                        @input="surveyInput">
            </treeselect>
          </div>
          <div class="col-1 ml-auto" v-bind:class="{ inlineSpinner: isBusy }"></div>
        </div>
      </template>

      <div v-if="surveySettings" class="d-flex flex-column">
        <collapsible-panel :value="true" accordion="surveySettings" :header="$t('Webportal.Administration.GeneralSettings')">
          <div class="form-group form-row">
            <label for="DefaultTargetGroup" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.DefaultTargetGroup') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.DefaultTargetGroup.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col-lg-4 col-md-6">
              <b-form-select id="DefaultTargetGroup"
                v-model="surveySettings.DefaultTargetGroup"
                v-validate data-vv-name="DefaultTargetGroup"
                size="sm"
                :disabled="isBusy">
                <b-form-select-option 
                  v-for="targetGroup in survey.TargetGroups"
                  :key="targetGroup.TargetGroup"
                  :value="targetGroup.TargetGroup">
                  {{ $t('Webportal.TargetGroup') }} {{ targetGroup.TargetGroup }} ({{ targetGroup.GroupName }})
                </b-form-select-option>
              </b-form-select>
            </div>
          </div>

          <hr />

          <!-- Checkdate -->
          <div class="form-group form-row">
            <label for="ShowCheckDate" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckDate') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckDate.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      :checked="getSettingValue('ShowCheckDate')"
                      @input="setSettingValue('ShowCheckDate', $event.target.checked)"
                      v-validate data-vv-name="ShowCheckDate"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
            <label class="col-auto ml-auto">
              {{ $t('Webportal.UseClientSetting') }}
              <font-awesome-icon :icon="['fas', 'lock']" />
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                       :checked="useClientSetting('ShowCheckDate')"
                       @input="resetSettingValue('ShowCheckDate', $event.target.checked)">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <!-- Checktime -->
          <div class="form-group form-row">
            <label for="ShowCheckTime" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckTime') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckTime.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      :checked="getSettingValue('ShowCheckTime')"
                      @input="setSettingValue('ShowCheckTime', $event.target.checked)"
                      v-validate data-vv-name="ShowCheckTime"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
            <label class="col-auto ml-auto">
              {{ $t('Webportal.UseClientSetting') }}
              <font-awesome-icon :icon="['fas', 'lock']" />
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                       :checked="useClientSetting('ShowCheckTime')"
                       @input="resetSettingValue('ShowCheckTime', $event.target.checked)">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <!-- Checkstatus -->
          <div class="form-group form-row">
            <label for="ShowCheckStatus" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckStatus') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckStatus.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      :checked="getSettingValue('ShowCheckStatus')"
                      @input="setSettingValue('ShowCheckStatus', $event.target.checked)"
                      v-validate data-vv-name="ShowCheckStatus"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
            <label class="col-auto ml-auto">
              {{ $t('Webportal.UseClientSetting') }}
              <font-awesome-icon :icon="['fas', 'lock']" />
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                       :checked="useClientSetting('ShowCheckStatus')"
                       @input="resetSettingValue('ShowCheckStatus', $event.target.checked)">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="ShowOverallScore" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowOverallScore') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowOverallScore.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="surveySettings.ShowOverallScore"
                      v-validate data-vv-name="ShowOverallScore"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="SurveyKpiId1" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.SurveyKpi1') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.SurveyKpi.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <treeselect v-model="surveySettings.SurveyKpiId1"
                          v-validate data-vv-name="SurveyKpiId1"
                          :placeholder="$t('Webportal.KpiFilter.Placeholder')"
                          :options="kpiItems"
                          :disabled="isBusy"
                          :disableFuzzyMatching="true"
                          :disable-branch-nodes="true"
                          :showCount="true"
                          showCountOf="LEAF_DESCENDANTS"
                          :defaultExpandLevel="1">
              </treeselect>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="SurveyKpiId2" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.SurveyKpi2') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.SurveyKpi.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <treeselect v-model="surveySettings.SurveyKpiId2"
                          v-validate data-vv-name="SurveyKpiId2"
                          :placeholder="$t('Webportal.KpiFilter.Placeholder')"
                          :options="kpiItems"
                          :disabled="isBusy"
                          :disableFuzzyMatching="true"
                          :disable-branch-nodes="true"
                          :showCount="true"
                          showCountOf="LEAF_DESCENDANTS"
                          :defaultExpandLevel="1">
              </treeselect>
            </div>
          </div>
        </collapsible-panel>

        <collapsible-panel accordion="surveySettings" :header="$t('Webportal.Administration.ScoreCalculation')">
          <!-- Overall calculation type -->
          <div class="form-group form-row">
            <label for="OverallCalculationType" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.OverallCalculationType') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationType.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <b-form-radio-group id="OverallCalculationType"
                                  buttons button-variant="outline-secondary" size="sm"
                                  v-model="surveySettings.OverallCalculationType"
                                  :disabled="isBusy"
                                  v-validate data-vv-name="OverallCalculationType"
                                  @change="overallCalculationTypeChanged">
                <b-form-radio :value="null">{{ $t('Webportal.Administration.OverallCalculationType.Client') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationType.Client.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                  </span>
                </b-form-radio>
                <b-form-radio :value="0">{{ $t('Webportal.Administration.OverallCalculationType.Standard') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationType.Standard.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                  </span>
                </b-form-radio>
                <b-form-radio :value="1">{{ $t('Webportal.Administration.OverallCalculationType.Weighted') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationType.Weighted.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                  </span>
                </b-form-radio>
                <b-form-radio :value="2">{{ $t('Webportal.Administration.OverallCalculationType.Average') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationType.Average.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                  </span>
                </b-form-radio>
              </b-form-radio-group>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="ShowOverallCalculationTypeDescription" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowOverallCalculationTypeDescription') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowOverallCalculationTypeDescription.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="surveySettings.ShowOverallCalculationTypeDescription"
                      v-validate data-vv-name="ShowOverallCalculationTypeDescription"
                      :disabled="isBusy || surveySettings.OverallCalculationType === undefined || surveySettings.OverallCalculationType === null">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <!-- Overall dynamic weighting -->
          <div class="form-group form-row">
            <label for="OverallCalculationDynamicWeighting" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.OverallCalculationDynamicWeighting') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationDynamicWeighting.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <b-form-radio-group id="OverallCalculationDynamicWeighting"
                                  buttons button-variant="outline-secondary" size="sm"
                                  v-model="surveySettings.OverallCalculationDynamicWeighting"
                                  :disabled="isBusy || surveySettings.OverallCalculationType !== 1"
                                  v-validate data-vv-name="OverallCalculationDynamicWeighting">
                <b-form-radio :value="0">{{ $t('Webportal.Administration.OverallCalculationDynamicWeighting.Off') }}</b-form-radio>
                <b-form-radio :value="1">{{ $t('Webportal.Administration.OverallCalculationDynamicWeighting.Factor') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationDynamicWeighting.Factor.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                  </span>
                </b-form-radio>
                <b-form-radio :value="2">{{ $t('Webportal.Administration.OverallCalculationDynamicWeighting.Percent') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.OverallCalculationDynamicWeighting.Percent.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                  </span>
                </b-form-radio>
              </b-form-radio-group>
            </div>
          </div>

          <hr />

          <!-- Scope of previous waves -->
          <div class="form-group form-row">
            <label for="ScopeOfPreviousWavesForCheckReport" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ScopeOfPreviousWavesForCheckReport') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ScopeOfPreviousWavesForCheckReport.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <b-form-radio-group id="ScopeOfPreviousWavesForCheckReport"
                                  buttons button-variant="outline-secondary" size="sm"
                                  v-model="surveySettings.ScopeOfPreviousWavesForCheckReport"
                                  :disabled="isBusy"
                                  v-validate="'required'"
                                  data-vv-name="ScopeOfPreviousWavesForCheckReport">
                <b-form-radio :value="0">{{ $t('Webportal.Administration.ScopeOfPreviousWavesForCheckReport.Survey') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ScopeOfPreviousWavesForCheckReport.Survey.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                  </span>
                </b-form-radio>
                <b-form-radio :value="1">{{ $t('Webportal.Administration.ScopeOfPreviousWavesForCheckReport.Project') }}
                  <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ScopeOfPreviousWavesForCheckReport.Project.Description')">
                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                  </span>
                </b-form-radio>
              </b-form-radio-group>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="NumberOfPreviousWavesForCheckReport" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.NumberOfPreviousWavesForCheckReport') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.NumberOfPreviousWavesForCheckReport.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <b-form-radio-group id="NumberOfPreviousWavesForCheckReport"
                                  buttons button-variant="outline-secondary" size="sm"
                                  v-model="surveySettings.NumberOfPreviousWavesForCheckReport"
                                  :options="previousWavesOptions"
                                  :disabled="isBusy"
                                  v-validate="'required'"
                                  data-vv-name="NumberOfPreviousWavesForCheckReport" />
            </div>
          </div>
        </collapsible-panel>

        <collapsible-panel accordion="surveySettings" :header="$t('Webportal.Administration.ScoreThresholds')"
          :headerDescription="$t('Webportal.Administration.ScoreThresholds.Description')">
          <div class="form-group form-row align-items-center">
            <label class="col-auto ml-auto">
              {{ $t('Webportal.UseClientSettings') }}
              <font-awesome-icon :icon="['fas', 'lock']" />
            </label>
            <div class="col-auto">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                       v-model="useCustomerThresholds">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>
          <div class="form-group form-row">
            <div class="col">
              <threshold-selector 
                v-model="surveyThresholds"
                v-validate data-vv-name="Thresholds" 
              />
            </div>
          </div>
        </collapsible-panel>

        <collapsible-panel accordion="surveySettings" :header="$t('Webportal.CheckReport')">
          <div class="form-group form-row">
            <label for="ShowCheckReportThresholdLegend" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckReportThresholdLegend') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportThresholdLegend.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="surveySettings.ShowCheckReportThresholdLegend"
                      v-validate data-vv-name="ShowCheckReportThresholdLegend"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="ShowCheckReportClusterOverview" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckReportClusterOverview') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportClusterOverview.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="surveySettings.ShowCheckReportClusterOverview"
                      v-validate data-vv-name="ShowCheckReportClusterOverview"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="ShowCheckReportWaveCompare" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckReportWaveCompare') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportWaveCompare.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="surveySettings.ShowCheckReportWaveCompare"
                      v-validate data-vv-name="ShowCheckReportWaveCompare"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="ShowCheckReportKpiOverview" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckReportKpiOverview') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportKpiOverview.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="surveySettings.ShowCheckReportKpiOverview"
                      v-validate data-vv-name="ShowCheckReportKpiOverview"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <hr />

          <div class="form-group form-row">
            <label for="ShowCheckReportClusterScore" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckReportClusterScore') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportClusterScore.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="surveySettings.ShowCheckReportClusterScore"
                      v-validate data-vv-name="ShowCheckReportClusterScore"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="ShowCheckReportClusterPoints" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckReportClusterPoints') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportClusterPoints.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="surveySettings.ShowCheckReportClusterPoints"
                      v-validate data-vv-name="ShowCheckReportClusterPoints"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <div class="form-group form-row">
            <label for="ShowCheckReportQuestionPoints" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.ShowCheckReportQuestionPoints') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.ShowCheckReportQuestionPoints.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <label class="switch switch-label switch-secondary">
                <input class="switch-input" type="checkbox"
                      v-model="surveySettings.ShowCheckReportQuestionPoints"
                      v-validate data-vv-name="ShowCheckReportQuestionPoints"
                      :disabled="isBusy">
                <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
              </label>
            </div>
          </div>

          <hr />

          <div class="form-group form-row">
            <label class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.InfoPage') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.InfoPage.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <image-uploader ref="infoPageUploader"
                              v-model="surveySettings.InfoPageUrl"
                              :upload-url="`api/administration/upload-info-page?survey-id=${surveySettings.SurveyId}`"
                              :previewHeight="150"
                              v-validate="'required'"
                              data-vv-name="InfoPage" />
            </div>
          </div>
        </collapsible-panel>

        <collapsible-panel accordion="surveySettings" :header="$t('Webportal.Actions.SurveyRevision')">
          <div class="form-group form-row">
            <label for="SurveyRevisionCreationLimitInDays" class="col-lg-4 col-md-6 col-form-label pt-0">
              {{ $t('Webportal.Administration.SurveyRevisionCreationLimitInDays') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.SurveyRevisionCreationLimitInDays.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </label>
            <div class="col">
              <dx-number-box v-model="surveySettings.SurveyRevisionCreationLimitInDays"
                            class="custom-control-inline" width="8em" height="2em"
                            v-validate data-vv-name="SurveyRevisionCreationLimitInDays" @input="void 0"
                            format="##0" :show-spin-buttons="true" :step="1" :min="1" :max="999"
                            :show-clear-button="true"
                            :disabled="isBusy" />
              {{ surveySettings.SurveyRevisionCreationLimitInDays > 1 ? $t('Webportal.NotificationSettings.Days') : $t('Webportal.NotificationSettings.Day') }}
            </div>
          </div>
        </collapsible-panel>
      </div>

      <template slot="footer">
        <div class="float-right">
          <button type="button" @click="cancel" class="btn btn-secondary" :disabled="!isFormChanged || isBusy">
            <font-awesome-icon :icon="['far', 'ban']" /> {{ $t('Webportal.Cancel') }}
          </button>
          <button type="button" @click="submit" class="btn btn-primary" :disabled="!isFormChanged || !isFormValid || isBusy">
            <font-awesome-icon :icon="['far', 'dot-circle']" /> {{ $t('Webportal.Save') }}
          </button>
        </div>
      </template>
    </b-card>
  </form>
</template>

<script>
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import SurveysService from '@/services/SurveysService';
import AdministationService from '@/services/AdministationService';
import EventBus from '@/services/EventBus.js';
import CollapsiblePanel from '@/components/CollapsiblePanel';
import ThresholdSelector from './ThresholdSelector';
import ImageUploader from './ImageUploader'
import { DxNumberBox } from 'devextreme-vue';

export default {
  name: "SurveySettings",

  mixins: [ AppConfig ],

  components: {
    CollapsiblePanel,
    ThresholdSelector,
    ImageUploader,
    DxNumberBox
  },

  data () {
    return {
      // Filter properties
      structureItems: [],                // Data items for structure treeselect
      surveyValue: null,                 // Selected survey item
      surveySettings: null,
      kpiItems: [],

      surveyThresholds: null,

      isBusy: false,

      previousWavesOptions: [
        { text: '1', value: 1 },
        { text: '2', value: 2 },
        { text: '3', value: 3 },
        { text: '4', value: 4 },
        { text: '5', value: 5 }
      ]
    }
  },

  mounted() {
    this.initializeTheme();
  },

  watch: {
    surveySettings (newVal, oldVal) {
      this.surveyThresholds =
        newVal?.Thresholds?.map(x => { return { value: { startValue: x.Minimum, endValue: x.Maximum }, color: x.Color, smiley: x.Smiley }});
    },

    surveyThresholds: {
      handler: function(newVal, oldVal) {
        if (this.surveySettings == null)
          return;

        this.surveySettings.Thresholds = 
          newVal?.map(x => { return { ScoreId: -1, Minimum: x.value.startValue, Maximum: x.value.endValue, Color: x.color, Smiley: x.smiley }});
      }, deep: true
    }
  },

  computed: {
    isFormChanged() {
      return Object.keys(this.validationFields).some(key => this.validationFields[key].dirty || this.validationFields[key].changed);
    },

    isFormValid() {
      return Object.keys(this.validationFields).every(key => this.validationFields[key].valid === null || this.validationFields[key].valid);
    },

    surveys() {
      // Returns an array of all surveys recursively for the given node
      const returnSurveys = (obj) => {
          return _.concat(_.startsWith(obj.id, 'S') ? obj : [],
              obj.children ? _.flatMapDeep(obj.children, returnSurveys) : []);
      };
      
      return _.flatMapDeep(this.structureItems, returnSurveys);
    },

    survey() {
      return this.surveys?.find(ss => ss.id === this.surveyValue);
    },

    useCustomerThresholds: {
      get: function() {
        return this.surveyThresholds === undefined || this.surveyThresholds === null;
      },
      set: function(newValue) {
        if (newValue) {
          this.surveyThresholds = null;
        }
        else {
          this.surveyThresholds = 
            this.surveySettings.Client?.Thresholds?.map(x => { return { value: { startValue: x.Minimum, endValue: x.Maximum }, color: x.Color, smiley: x.Smiley }});
        }
      }
    }
  },

  activated() {
    this.getSurveyStructure();
    this.getSurveySettings();
    EventBus.$on('LocalizationChanged', this.localizationChanged);
  },

  deactivated() {
    EventBus.$off('LocalizationChanged', this.localizationChanged);
  },

  beforeRouteLeave (to, from, next) {
    this.$emit('unsavedChanges', this.isFormChanged, next);
  },

  methods: {
    localizationChanged(localizationId) {
      this.getSurveyStructure();
    },

    getSettingValue(settingName) {
      return this.surveySettings[settingName] ?? this.surveySettings.Client[settingName];
    },

    setSettingValue(settingName, newValue) {
      this.surveySettings[settingName] = newValue;
      this.validationFields[settingName].dirty = true;
    },

    useClientSetting(settingName) {
      return this.surveySettings[settingName] === undefined || this.surveySettings[settingName] === null;
    },

    resetSettingValue(settingName, useClientSetting) {
      this.setSettingValue(settingName, useClientSetting ? null : this.surveySettings.Client[settingName]);
    },

    // Get survey structure data for the structure treeselect
    getSurveyStructure() {
      SurveysService.getSurveyStructure()
        .then(response => {
          this.structureItems = response.data;

          if (!this.surveyValue)
          {
            let firstSurvey = _.first(this.surveys);
            this.surveyInput(firstSurvey ? firstSurvey.id : null);
          }
        })
    },

    getSurveySettings() {
      if (this.surveyValue)
      {
        Promise.all([
          AdministationService.getSurveySettings(this.surveyValue.substring(1)),
          SurveysService.getSurveyKpis({ surveyIds: [ this.surveyValue.substring(1) ] })
          ])
          .then(([responseSurveySettings, responseSurveyKpis])  => {
            this.isBusy = false;

            this.surveySettings = responseSurveySettings.data ? responseSurveySettings.data[0] : null;
            if (this.surveySettings)
            {
              // Set ShowOverallCalculationTypeDescription to client value when not defined
              this.surveySettings.ShowOverallCalculationTypeDescription = 
                this.surveySettings.ShowOverallCalculationTypeDescription ?? this.surveySettings.Client.ShowOverallCalculationTypeDescription;

              this.surveySettings.InfoPageUrl = 
                this.surveySettings.InfoPageExternalImageId ?
                  process.env.BASE_URL + `api/media/image?id=${this.surveySettings.InfoPageExternalImageId}` :
                  ' ';
            }

            if (responseSurveyKpis.data && responseSurveyKpis.data.length === 1 && responseSurveyKpis.data[0].children) {
              this.kpiItems = responseSurveyKpis.data[0].children;
              this.kpiItems?.forEach(kpiType => 
                kpiType?.children.forEach(kpiGroup => 
                  kpiGroup?.children.forEach(kpi => kpi.id = kpi.id.substring(1))
                )
              );
            } else {
              this.kpiItems = [];
            }

            this.$validator.reset();
          });
      }
      else
      {
        this.surveySettings = null;
        this.$validator.reset();
      }
    },

    // Called whenever the input of the structure treeselect changes
    surveyInput(value) {
      if (this.surveyValue !== value)
      {
        let currentValue = this.surveyValue;
        this.surveyValue = value;
        
        this.$emit('unsavedChanges', this.isFormChanged, result => {
          if (result === false)
          {
            this.surveyValue = currentValue;
          }
          else
            this.getSurveySettings();
        });
      }
    },

    overallCalculationTypeChanged(overallCalculationType) {
      this.surveySettings.OverallCalculationDynamicWeighting = overallCalculationType == 1 ? 2 : null;
      if (overallCalculationType === undefined || overallCalculationType === null)
        this.surveySettings.ShowOverallCalculationTypeDescription = 
          this.surveySettings.Client.ShowOverallCalculationTypeDescription;
    },

    cancel() {
      this.$emit('unsavedChanges', this.isFormChanged, result => {
        if (result !== false) this.getSurveySettings();
      });
    },

    submit() {
      this.isBusy = true;

      // Clone settings for submission
      let surveySettings = { ... this.surveySettings };

      // Replace undefined with null
      surveySettings.SurveyKpiId1 = this.surveySettings.SurveyKpiId1 ?? null;
      surveySettings.SurveyKpiId2 = this.surveySettings.SurveyKpiId2 ?? null;
      surveySettings.OverallCalculationType = this.surveySettings.OverallCalculationType ?? null;
      surveySettings.OverallCalculationDynamicWeighting = this.surveySettings.OverallCalculationDynamicWeighting ?? null;

      // Set ShowOverallCalculationTypeDescription to null when overall calculation type is set to client value
      surveySettings.ShowOverallCalculationTypeDescription = 
        surveySettings.OverallCalculationType !== null ? surveySettings.ShowOverallCalculationTypeDescription : null;

      surveySettings.Thresholds = surveySettings.Thresholds ?? [ { ScoreId: -1 } ]; // Delete existing thresholds

      AdministationService.setSurveySettings(surveySettings)
        .then(response => {
          if (this.validationFields['InfoPage'].dirty)
            this.$refs.infoPageUploader.uploadImage();

          this.isBusy = false;
          this.$validator.reset();
        })
        .catch(_ => { this.isBusy = false; } );
    }
  }
}
</script>
